/* Biotype Intensive — animation utilities (v2)
   - Reveal on scroll (stagger via data-bi-reveal-delay)
   - Counters (data-count, data-count-suffix, data-count-prefix)
   - Premium countdown with flip cards (data-bi-countdown-target)
   - Tilt 3D on hover (data-bi-tilt)
   - Parallax soft (data-bi-parallax="0.15")
   - SVG line draw (data-bi-svg-draw)
   - Sticky CTA visibility
*/
(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- 1. Reveal on scroll ----------
  const revealEls = document.querySelectorAll('[data-bi-reveal]');
  if ('IntersectionObserver' in window && revealEls.length) {
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          const delay = entry.target.getAttribute('data-bi-reveal-delay') || 0;
          setTimeout(function () {
            entry.target.classList.add('bi-revealed');
          }, parseInt(delay));
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    revealEls.forEach(function (el) { io.observe(el); });
  } else {
    revealEls.forEach(function (el) { el.classList.add('bi-revealed'); });
  }

  // ---------- 2. Animated counters ----------
  const counterEls = document.querySelectorAll('[data-count]');
  function animateCounter(el) {
    const target = parseFloat(el.getAttribute('data-count')) || 0;
    const suffix = el.getAttribute('data-count-suffix') || '';
    const prefix = el.getAttribute('data-count-prefix') || '';
    const duration = parseInt(el.getAttribute('data-count-duration')) || 1800;
    const start = performance.now();
    function tick(now) {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = Math.floor(eased * target);
      el.textContent = prefix + value.toLocaleString('it-IT') + suffix;
      if (t < 1) requestAnimationFrame(tick);
      else el.textContent = prefix + target.toLocaleString('it-IT') + suffix;
    }
    requestAnimationFrame(tick);
  }
  if ('IntersectionObserver' in window && counterEls.length) {
    const counterIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          counterIo.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    counterEls.forEach(function (el) { counterIo.observe(el); });
  }

  // ---------- 3. Premium countdown with flip ----------
  const countdownEls = document.querySelectorAll('[data-bi-countdown-target]');
  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function setFlipValue(cell, value) {
    const current = cell.querySelector('.bi-flip__current');
    const next = cell.querySelector('.bi-flip__next');
    if (!current || !next) return;
    if (current.textContent === value) return;
    next.textContent = value;
    cell.classList.remove('bi-flip-anim');
    // Force reflow
    void cell.offsetWidth;
    cell.classList.add('bi-flip-anim');
    setTimeout(function () {
      current.textContent = value;
      cell.classList.remove('bi-flip-anim');
    }, 600);
  }
  function updateCountdown(el) {
    const target = new Date(el.getAttribute('data-bi-countdown-target')).getTime();
    const now = Date.now();
    let diff = Math.max(0, target - now);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    diff -= days * 1000 * 60 * 60 * 24;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    diff -= hours * 1000 * 60 * 60;
    const mins = Math.floor(diff / (1000 * 60));
    diff -= mins * 1000 * 60;
    const secs = Math.floor(diff / 1000);
    const cells = el.querySelectorAll('[data-bi-flip]');
    if (cells.length >= 4) {
      setFlipValue(cells[0], pad(days));
      setFlipValue(cells[1], pad(hours));
      setFlipValue(cells[2], pad(mins));
      setFlipValue(cells[3], pad(secs));
    }
  }
  if (countdownEls.length) {
    countdownEls.forEach(updateCountdown);
    setInterval(function () { countdownEls.forEach(updateCountdown); }, 1000);
  }

  // ---------- 4. Tilt 3D on hover ----------
  if (!prefersReducedMotion) {
    const tiltEls = document.querySelectorAll('[data-bi-tilt]');
    tiltEls.forEach(function (el) {
      const max = parseFloat(el.getAttribute('data-bi-tilt')) || 6;
      el.style.transformStyle = 'preserve-3d';
      el.style.transition = 'transform .2s ease-out';
      el.addEventListener('mousemove', function (e) {
        const rect = el.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        const rotY = (x - 0.5) * max * 2;
        const rotX = (0.5 - y) * max * 2;
        el.style.transform = 'perspective(1200px) rotateX(' + rotX + 'deg) rotateY(' + rotY + 'deg) translateY(-4px)';
      });
      el.addEventListener('mouseleave', function () {
        el.style.transform = '';
      });
    });
  }

  // ---------- 5. Parallax soft ----------
  if (!prefersReducedMotion) {
    const parallaxEls = document.querySelectorAll('[data-bi-parallax]');
    if (parallaxEls.length) {
      function updateParallax() {
        parallaxEls.forEach(function (el) {
          const speed = parseFloat(el.getAttribute('data-bi-parallax')) || 0.15;
          const rect = el.getBoundingClientRect();
          const winH = window.innerHeight;
          if (rect.bottom < 0 || rect.top > winH) return;
          const offset = (rect.top - winH / 2) * speed * -1;
          el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
        });
      }
      window.addEventListener('scroll', updateParallax, { passive: true });
      window.addEventListener('resize', updateParallax);
      updateParallax();
    }
  }

  // ---------- 6. SVG line draw on reveal ----------
  const svgDrawEls = document.querySelectorAll('[data-bi-svg-draw]');
  svgDrawEls.forEach(function (svgWrap) {
    const paths = svgWrap.querySelectorAll('path, circle, rect, line, polyline, polygon');
    paths.forEach(function (p) {
      try {
        const len = p.getTotalLength ? p.getTotalLength() : 200;
        p.style.strokeDasharray = len;
        p.style.strokeDashoffset = len;
        p.style.transition = 'stroke-dashoffset 1.4s cubic-bezier(.6,.05,.3,1)';
      } catch (e) { /* ignore non-path elements */ }
    });
  });
  if ('IntersectionObserver' in window && svgDrawEls.length) {
    const drawIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          const paths = entry.target.querySelectorAll('path, circle, rect, line, polyline, polygon');
          paths.forEach(function (p, i) {
            setTimeout(function () { p.style.strokeDashoffset = '0'; }, i * 120);
          });
          drawIo.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    svgDrawEls.forEach(function (el) { drawIo.observe(el); });
  }

  // ---------- 7. Sticky CTA visibility ----------
  const stickyBars = document.querySelectorAll('[data-bi-sticky]');
  if (stickyBars.length) {
    function onScroll() {
      const scrolled = window.scrollY;
      const docH = document.documentElement.scrollHeight;
      const winH = window.innerHeight;
      const ratio = scrolled / Math.max(1, (docH - winH));
      stickyBars.forEach(function (bar) {
        if (ratio > 0.18 && ratio < 0.92) bar.classList.add('bi-sticky-visible');
        else bar.classList.remove('bi-sticky-visible');
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ---------- 8. Timeline progress (program) ----------
  const timelines = document.querySelectorAll('[data-bi-timeline]');
  if ('IntersectionObserver' in window && timelines.length) {
    timelines.forEach(function (timeline) {
      const items = timeline.querySelectorAll('[data-bi-timeline-item]');
      const itemIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('bi-timeline-active');
          }
        });
      }, { threshold: 0.5, rootMargin: '0px 0px -20% 0px' });
      items.forEach(function (item) { itemIo.observe(item); });
    });
  }
})();
